import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import ApiService from "@/core/services/ApiService";
import { AxiosRequestConfig } from "axios";
import { Company } from "@/core/services/api/interfaces/common/Company";
import {
  CompanyEmployee,
  CompanyPaymentService,
  CompanyProfile,
  OnboardingTransition,
} from "@/core/services/api/interfaces/admin/CompanyProfile";
import { ComplianceTracking } from "@/core/services/api/interfaces/manager/ComplianceTracking";

@Module
export default class CompaniesModule extends VuexModule {
  pagination = new PaginationResponse<Company>();
  loading = false;

  get companies(): Company[] {
    return this.companiesPagination.data;
  }

  get companiesLoading(): boolean {
    return this.loading;
  }

  get companiesPagination(): PaginationResponse<Company> {
    return this.pagination;
  }

  get companiesTotal(): number {
    return this.pagination.total;
  }

  get companiesRequired(): boolean {
    return !this.pagination.total && !this.loading;
  }

  get companiesCurrentPage(): number {
    return this.pagination.current_page;
  }

  get companiesPerPage(): number {
    return this.pagination.per_page;
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANIES_LOAD](params) {
    this.context.commit(Mutations.ADMIN_COMPANIES_SET_LOADING, true);
    ApiService.admin.company
      .list({
        params: params,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse)
      .then((response) => {
        this.context.commit(Mutations.ADMIN_COMPANIES_SET, response);
      })
      .catch(console.error)
      .finally(() => {
        this.context.commit(Mutations.ADMIN_COMPANIES_SET_LOADING, false);

        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.ADMIN_COMPANIES_LOAD
        );
      });
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PROFILE_REQUEST](id: string) {
    return ApiService.admin.company
      .profile(id)
      .then(ApiService.unwrapItemResponse<CompanyProfile>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_BY_ZOHO_ID_REQUEST](zoho_id: string) {
    return ApiService.admin.company
      .viewByZohoId(zoho_id)
      .then(ApiService.unwrapItemResponse<CompanyProfile>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_EMPLOYEES_REQUEST](id: string) {
    return ApiService.admin.company
      .employees(id)
      .then(ApiService.unwrapItemsResponse<CompanyEmployee>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_COMPLIANCES_REQUEST](id: string) {
    return ApiService.admin.company
      .compliances(id)
      .then(ApiService.unwrapItemsResponse<ComplianceTracking>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PAYMENT_SERVICES_REQUEST](id: string) {
    return ApiService.admin.company
      .paymentServices(id)
      .then(ApiService.unwrapItemsResponse<CompanyPaymentService>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PAYMENT_SERVICE_ONBOARDING_TRANSITIONS_REQUEST]({
    id,
    payment_service,
  }: {
    id: string;
    payment_service: string;
  }) {
    console.log(id, payment_service);
    return ApiService.admin.company
      .onboardingTransitions(id, payment_service)
      .then(ApiService.unwrapItemsResponse<OnboardingTransition>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PAYMENT_SERVICE_UPDATE_FIELDS_ONBOARDING_STAGE]({
    company_id,
    payment_service,
    transition_id,
    data,
  }: {
    company_id: string;
    payment_service: string;
    transition_id: string;
    data: { [key: string]: string | number | null };
  }) {
    console.log(company_id, payment_service, transition_id);
    return ApiService.admin.company
      .updateOnboardingTransitionFields(
        company_id,
        payment_service,
        transition_id,
        data as AxiosRequestConfig
      )
      .then(ApiService.unwrapItemsResponse<OnboardingTransition>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PAYMENT_SERVICE_APPLY_ONBOARDING_STAGE_REQUEST]({
    company_id,
    payment_service,
    next_stage,
  }: {
    company_id: string;
    payment_service: string;
    next_stage: string;
  }) {
    return ApiService.admin.company
      .applyNextOnboardingTransition(company_id, payment_service, next_stage)
      .then(ApiService.unwrapItemsResponse<OnboardingTransition>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_PAYMENT_SERVICE_ONBOARD_REQUEST]({
    company_id,
    payment_service,
  }: {
    company_id: string;
    payment_service: string;
  }) {
    return ApiService.admin.company
      .startOnboarding(company_id, payment_service)
      .then(ApiService.unwrapItemsResponse<OnboardingTransition>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_UPDATE]({ id, params }) {
    return ApiService.admin.company
      .update(id, params)
      .then(ApiService.unwrapItemResponse<CompanyProfile>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_EMPLOYEE_COMPLIANCES_REQUEST]({
    company_id,
    employee_id,
    params,
  }) {
    return ApiService.admin.company
      .employeeCompliances(company_id, employee_id, params)
      .then(ApiService.unwrapItemsResponse<ComplianceTracking>);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_COMPANY_EMPLOYEE_UPDATE]({ company_id, employee_id, params }) {
    return ApiService.admin.company
      .updateEmployee(company_id, employee_id, params)
      .then(ApiService.unwrapItemResponse<CompanyEmployee>);
  }

  @Mutation
  [Mutations.ADMIN_COMPANIES_SET](pagination: PaginationResponse<Company>) {
    this.pagination = pagination;
  }

  @Mutation
  [Mutations.ADMIN_COMPANIES_RESET]() {
    this.pagination = new PaginationResponse<Company>();
  }

  @Mutation
  [Mutations.ADMIN_COMPANIES_SET_LOADING](loading: boolean) {
    this.loading = loading;
  }
}
